// lib/content-engine.ts
import { blogs } from "@/lib/content";

export type ContentType = 'case-study' | 'article' | 'playbook';

export interface ContentItem {
  slug: string;
  type: ContentType;
  title: string;
  seoTitle?: string;
  seoDescription?: string;
  clientName?: string;
  date: string;
  category: string;
  industry?: string;
  role?: string;
  excerpt: string;
  logo?: string;
  
  // Changed from architectureImage to reflect reality
  resultsImage?: string; 

  atAGlance?: {
    challenge: string;
    solution: string;
    outcome: string;
  };
  aboutClient?: string;
  
  challenge?: string;
  challengeBullets?: string[];
  
  solution?: string;
  solutionBullets?: string[];
  
  whyAllCode?: string;
  impactSummary?: string[];

  results?: { metric: string; label: string }[];
  techStack?: string[];
  testimonial?: { quote: string; author: string; role: string };
  
  content?: string; 
}

const caseStudies: ContentItem[] = [
  // 1. CloudContactAI
  {
    slug: "aws-genai-sms-compliance-automation",
    type: "case-study",
    clientName: "CloudContactAI",
    title: "Automating SMS Compliance with Amazon Nova Pro",
    seoTitle: "AWS GenAI Compliance Automation | CloudContactAI Case Study",
    seoDescription: "Discover how AllCode reduced SMS noise by 80% using Amazon Nova Pro and AWS Bedrock.",
    date: "2024-01-15",
    category: "GenAI",
    industry: "Marketing Tech",
    role: "GenAI Compliance",
    excerpt: "Transformed manual message triage into an automated, serverless compliance workflow using Amazon Bedrock.",
    logo: "https://cdn.brandfetch.io/cloudcontactai.com",
    // Placeholder for your Results/Metrics graphic
    resultsImage: "https://placehold.co/800x450/f8fafc/475569?text=Results+%26+Metrics+Graphic", 
    
    atAGlance: {
      challenge: "High volume of non-actionable 'noise' in inbound SMS streams.",
      solution: "GenAI-powered compliance filtering using Amazon Nova Pro.",
      outcome: "Transformed manual message triage into an automated, serverless compliance workflow."
    },
    aboutClient: "CloudContactAI (CCAI) is a leading AI-powered SMS and email marketing platform. They help businesses engage customers through intelligent, automated campaigns and seamless messaging workflows.",
    
    challenge: "With thousands of SMS campaigns launching daily, CloudContactAI faced a significant operational bottleneck: inbound noise. While outbound messaging was highly optimized, inbound channels were flooded with automated replies, carrier notifications, and system alerts.",
    challengeBullets: [
      "Operational Drag: Agents wasted hours manually sifting through noise to find human engagement.",
      "Compliance Risk: High volumes made it difficult to spot 'Stop' or 'Opt-out' signals accurately, introducing legal liability.",
      "Scalability Limit: Increasing client volume meant linearly increasing support staff just to filter spam."
    ],

    solution: "CloudContactAI partnered with AllCode to engineer a scalable, serverless compliance engine. Recognizing the nuance required to distinguish between a 'hard bounce' and an angry customer, AllCode architected a solution using Generative AI on AWS.",
    solutionBullets: [
      "Ingestion & Routing: Inbound SMS traffic is captured via Amazon SNS and processed by AWS Lambda.",
      "The Intelligence Layer: The core utilizes Amazon Nova Pro via AWS Bedrock. AllCode engineered deterministic prompts to classify messages with high precision.",
      "Data Persistence: Metadata and compliance decisions are stored in Amazon DynamoDB for real-time state management.",
      // CRITICAL FOR AWS COMPETENCY
      "Guardrails & Validation: Implemented Guardrails for Amazon Bedrock to filter PII and validated model accuracy against a golden dataset of historical messages."
    ],

    whyAllCode: "CloudContactAI needed more than just a developer; they needed cloud architects familiar with the bleeding edge of AWS GenAI. AllCode’s status as an Advanced AWS Partner provided the expertise required to implement Amazon Nova Pro securely and cost-effectively.",

    impactSummary: [
      "Inbox Sanitation: Automated noise filtering removed non-actionable messages from agent queues.",
      "Risk Mitigation: 'Do Not Contact' requests are now detected and processed instantly.",
      "Scalable Operations: The system handles fluctuating volumes of inbound SMS without requiring additional human headcount."
    ],

    results: [
      { metric: "80%", label: "Reduction in Noise" },
      { metric: "75%", label: "Manual Review Saved" },
      { metric: "$100k+", label: "Sales Growth" },
    ],
    techStack: ["Amazon Bedrock", "Amazon Nova Pro", "AWS Lambda", "Amazon DynamoDB"]
  },

  // 2. Let's Play Soccer
  {
    slug: "aws-ai-agents-customer-support-automation",
    type: "case-study",
    clientName: "Let's Play Soccer",
    title: "AI Agents for 24/7 Customer Support",
    seoTitle: "AI Agents for Customer Support | Let’s Play Soccer Case Study",
    seoDescription: "See how AllCode implemented 24/7 AI Agents using Amazon Bedrock AgentCore to automate support and deflect 70% of tickets.",
    date: "2024-02-10",
    category: "AI Agents",
    industry: "Sports Management",
    role: "AI Automation",
    excerpt: "Enabled instant, 24/7 automated resolution for thousands of player and team inquiries using Amazon Bedrock AgentCore.",
    logo: "https://cdn.brandfetch.io/letsplaysoccer.com",
    resultsImage: "https://placehold.co/800x450/f8fafc/475569?text=Results+%26+Metrics+Graphic", 

    atAGlance: {
      challenge: "Overwhelming volume of manual administrative inquiries and scheduling requests.",
      solution: "AI Automation Agents using Amazon Bedrock AgentCore.",
      outcome: "Enabled instant, 24/7 automated resolution for thousands of player and team inquiries."
    },
    aboutClient: "Let’s Play Soccer is a nationwide soccer league and facility network providing organized recreational soccer, team management tools, and match scheduling for players across multiple U.S. regions.",

    challenge: "Despite strong demand, the organization faced challenges managing the sheer volume of player interactions. Manual handling of team requests, rule clarifications, and scheduling changes was creating friction.",
    challengeBullets: [
      "Bottlenecked Staff: Valuable staff time was consumed answering repetitive questions.",
      "Delayed Responses: Players often had to wait for business hours to get answers to simple questions.",
      "Lack of Standardization: Manual responses occasionally led to inconsistent application of league rules."
    ],

    solution: "Let’s Play Soccer engaged AllCode to architect an intelligent automation layer. The goal was to build 'AI Agents' capable of understanding context, consulting rulebooks, and executing decisions without human intervention.",
    solutionBullets: [
      "The Brain (AgentCore & Strands): Using the Strands Agents SDK, AllCode defined agent workflows and logic models that could 'reason' through player requests.",
      "The Knowledge Base: Agents were connected to Amazon DynamoDB to access real-time league data, schedules, and rule sets.",
      "Secure Execution: The system uses AWS Lambda and API Gateway to securely trigger agents from web forms.",
      // CRITICAL FOR AWS COMPETENCY
      "Guardrails & Safety: Implemented Guardrails for Amazon Bedrock to redact PII and enforce topic boundaries, ensuring agents never hallucinate scheduling conflicts."
    ],

    whyAllCode: "Let’s Play Soccer needed a partner capable of building complex, logic-driven AI, not just simple chatbots. AllCode’s experience with Agentic Workflows allowed them to build a system that could actually do work, not just talk.",

    impactSummary: [
      "24/7 Availability: Players get answers to rule and schedule questions instantly, day or night.",
      "Consistent Decisions: AI agents apply league rules uniformly, eliminating human bias or error.",
      "Enterprise Scalability: The serverless architecture allows the league to expand to new regions without linearly increasing support costs."
    ],

    results: [
      { metric: "70%", label: "Ticket Deflection" },
      { metric: "85%", label: "Routine Automation" },
      { metric: "35%", label: "Op-Ex Reduction" },
    ],
    techStack: ["Bedrock AgentCore", "Strands SDK", "AWS Lambda", "Amazon DynamoDB"]
  },

  // 3. Bayview Solutions
  {
    slug: "ai-code-review-automation-github-aws",
    type: "case-study",
    clientName: "Bayview Solutions",
    title: "Automated AI Code Review Agents",
    seoTitle: "Automated AI Code Review Agents | Bayview Solutions Case Study",
    seoDescription: "Learn how AllCode accelerated software delivery by 45% with a custom GitHub-integrated AI Code Review Agent.",
    date: "2024-03-01",
    category: "DevOps",
    industry: "FinTech",
    role: "DevOps Automation",
    excerpt: "Streamlined the software development lifecycle with instant, automated feedback on every Pull Request.",
    logo: "https://cdn.brandfetch.io/bayviewsolutions.com",
    resultsImage: "https://placehold.co/800x450/f8fafc/475569?text=Results+%26+Metrics+Graphic", 

    atAGlance: {
      challenge: "Manual code reviews creating bottlenecks and inconsistent quality in software delivery.",
      solution: "GitHub-integrated AI Code Review Agent using Claude 3.5.",
      outcome: "Streamlined the software development lifecycle with instant, automated feedback on every Pull Request."
    },
    aboutClient: "Bayview Solutions is a specialized financial services firm focused on accounts receivable solutions, distressed asset acquisition, and debt recovery.",

    challenge: "As Bayview’s software engineering needs grew, their manual code review process became a constraint. Managing multiple internal tools and client integrations meant that senior engineers were spending disproportionate time reviewing code rather than building features.",
    challengeBullets: [
      "Slow Feedback Loops: Developers often waited days for feedback on Pull Requests (PRs).",
      "Inconsistent Quality: Review rigor varied depending on which engineer was available.",
      "Security Risks: Manual reviews occasionally missed subtle security vulnerabilities or best-practice violations."
    ],

    solution: "Bayview engaged AllCode to implement an automated 'AI Pair Programmer' that could review code 24/7. AllCode architected a solution that integrates directly into the developer's native workflow on GitHub.",
    solutionBullets: [
      "Event Trigger: A GitHub Webhook triggers the workflow whenever a PR is opened or updated.",
      "Analysis Engine: AWS Lambda receives the payload and invokes Amazon Bedrock, utilizing the Claude 3.5 Haiku model.",
      "Contextual Memory: The agent utilizes Amazon S3 and DynamoDB to store context to avoid repetitive feedback.",
      // CRITICAL FOR AWS COMPETENCY
      "Guardrails & Security: Implemented Guardrails for Amazon Bedrock to ensure no PII is logged and that the model adheres to code analysis."
    ],

    whyAllCode: "Bayview required a solution that integrated with their existing CI/CD pipelines without disrupting developer habits. AllCode’s 'Reference Implementation' for AI Agents allowed for a rapid deployment customized to Bayview’s coding standards.",

    impactSummary: [
      "Instant Feedback: Developers receive detailed code analysis in seconds, not days.",
      "Standardized Quality: The AI enforces a consistent baseline of quality and security across all repositories.",
      "Security Hardening: Early detection of potential vulnerabilities prevents bad code from ever reaching production."
    ],

    results: [
      { metric: "45%", label: "Faster Cycle Time" },
      { metric: "32%", label: "Fewer Defects" },
      { metric: "<1m", label: "Avg Review Time" },
    ],
    techStack: ["Amazon Bedrock", "Claude 3.5 Haiku", "AWS Lambda", "API Gateway", "GitHub Actions"]
  },

  // --- EXISTING LEGACY STUDIES ---
  {
    slug: "remitter",
    type: "case-study",
    title: "Scaling Fintech Infrastructure for High-Volume Processing",
    clientName: "Remitter",
    industry: "Fintech",
    date: "2023-10-01",
    category: "Fintech",
    role: "Fintech Scalability",
    excerpt: "Remitter needed to handle a massive influx of transaction data while ensuring 99.99% availability.",
    logo: "https://cdn.brandfetch.io/remitter.com",
    challenge: "Remitter needed to handle a massive influx of transaction data while ensuring 99.99% availability.",
    solution: "AllCode re-architected the core platform using AWS Serverless technologies (Lambda, Fargate).",
    results: [
      { metric: "50%", label: "Lower AWS Costs" },
      { metric: "99.99%", label: "Uptime" },
      { metric: "10x", label: "Speed" },
    ],
    techStack: ["AWS Lambda", "Node.js", "Aurora DB", "Terraform"]
  },
  {
    slug: "trueaccord",
    type: "case-study",
    title: "AI-Driven Debt Collection at Enterprise Scale",
    clientName: "TrueAccord",
    industry: "Fintech / AI",
    date: "2023-09-15",
    category: "Fintech",
    role: "AI Debt Collection",
    excerpt: "TrueAccord required a highly secure, compliant environment to train ML models.",
    logo: "https://cdn.brandfetch.io/trueaccord.com",
    challenge: "TrueAccord required a highly secure, compliant environment to train ML models.",
    solution: "We deployed a secure MLOps pipeline on AWS, utilizing SageMaker for model training.",
    results: [
      { metric: "3x", label: "Faster Deployment" },
      { metric: "100%", label: "Compliance" },
      { metric: "24/7", label: "Monitoring" },
    ],
    techStack: ["AWS SageMaker", "Kubernetes", "Python", "Kafka"]
  },
  {
    slug: "homebase",
    type: "case-study",
    title: "Mobile Engineering for Modern Workforce Management",
    clientName: "Homebase",
    industry: "SaaS / HR Tech",
    date: "2023-07-10",
    category: "SaaS",
    role: "Mobile Engineering",
    excerpt: "Homebase needed to unify their iOS and Android experiences.",
    logo: "https://cdn.brandfetch.io/joinhomebase.com",
    results: [
      { metric: "4.8", label: "App Store Rating" },
      { metric: "2M+", label: "Daily Active Users" },
      { metric: "30%", label: "Faster Feature Release" },
    ],
    techStack: ["Swift", "Kotlin", "GraphQL", "AWS AppSync"]
  },
  {
    slug: "driftwood",
    type: "case-study",
    title: "Investment Data Platform & Graph Modeling",
    clientName: "Driftwood Capital",
    industry: "Real Estate / Fintech",
    date: "2023-08-20",
    category: "Real Estate",
    role: "Data Engineering",
    excerpt: "Driftwood needed to aggregate complex investment data from disparate sources.",
    logo: "https://cdn.brandfetch.io/driftwoodcapital.com",
    results: [
      { metric: "100%", label: "Data Integrity" },
      { metric: "Real-time", label: "Investor Reporting" },
      { metric: "Secure", label: "Role-Based Access" },
    ],
    techStack: ["AWS Glue", "React", "GraphDB", "Python"]
  },
  {
    slug: "leadiq",
    type: "case-study",
    title: "Scaling Data Ingestion",
    clientName: "LeadIQ",
    date: "2023-06-15",
    category: "SaaS",
    role: "Serverless Arch",
    excerpt: "Re-architecting a monolithic ingestion pipeline to serverless.",
    logo: "https://cdn.brandfetch.io/leadiq.com",
    techStack: ["AWS", "Serverless", "Node.js"]
  },
  {
    slug: "bluemercury",
    type: "case-study",
    title: "Mobile Commerce Experience",
    clientName: "BlueMercury",
    date: "2023-05-20",
    category: "Retail",
    role: "Mobile App",
    excerpt: "Built the flagship mobile retail experience with real-time inventory sync.",
    logo: "https://cdn.brandfetch.io/bluemercury.com",
    techStack: ["Mobile", "React Native", "iOS"]
  },
  {
    slug: "globalstar",
    type: "case-study",
    title: "Satellite Communication Portal",
    clientName: "Globalstar",
    date: "2023-04-10",
    category: "Telecom",
    role: "Web Portal",
    excerpt: "Enterprise customer portal for managing satellite communications hardware.",
    logo: "https://cdn.brandfetch.io/globalstar.com",
    techStack: ["Python", "Django", "AWS"]
  },
  {
    slug: "ten-x",
    type: "case-study",
    title: "Real Estate Auction Platform",
    clientName: "Ten-X",
    date: "2023-03-05",
    category: "Real Estate",
    role: "Frontend Arch",
    excerpt: "Modernizing the frontend architecture for the world's largest online real estate auction platform.",
    logo: "https://cdn.brandfetch.io/ten-x.com",
    techStack: ["React", "High Scale", "Web"]
  },
  {
    slug: "jitterbit",
    type: "case-study",
    title: "API Integration Engine",
    clientName: "Jitterbit",
    date: "2023-02-15",
    category: "DevOps",
    role: "Infrastructure",
    excerpt: "Automating infrastructure deployment and scaling for a leading API integration platform.",
    logo: "https://cdn.brandfetch.io/jitterbit.com",
    techStack: ["DevOps", "AWS", "Terraform"]
  },
  {
    slug: "gametime",
    type: "case-study",
    title: "Last-Minute Ticketing",
    clientName: "Gametime",
    date: "2023-01-20",
    category: "Consumer",
    role: "Mobile Perf",
    excerpt: "Optimizing the Android application performance for high-traffic game day usage.",
    logo: "https://cdn.brandfetch.io/gametime.co",
    techStack: ["Mobile", "Android", "Performance"]
  }
];

const articles: ContentItem[] = blogs.map((post) => ({
  slug: post.slug,
  type: "article",
  title: post.title,
  date: post.date,
  category: post.category,
  excerpt: post.excerpt,
  content: post.content,
}));

export const allContent: ContentItem[] = [...caseStudies, ...articles];

export function getCaseStudies() {
  return allContent.filter(item => item.type === 'case-study');
}

export function getArticles() {
  return allContent.filter(item => item.type === 'article' || item.type === 'playbook');
}

export function getContentBySlug(slug: string) {
  return allContent.find(item => item.slug === slug);
}